import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useIntl } from "../contexts/IntlContext";

type UseYoutubeOAuthPopupOptions = {
  start: (popup: boolean) => Promise<{ authorizationUrl?: string | null }>;
  /** Polling de fallback enquanto o popup está aberto — deve refletir se a conexão já foi concluída. */
  getConnected: () => Promise<boolean>;
  onConnected: () => Promise<void> | void;
};

/**
 * Abre o popup de consentimento do Google, aplica o polling de fallback (a cada 2s por até 5min)
 * e escuta o `postMessage` que o callback OAuth da camera-meulance dispara ao terminar
 * ({@code source: "meulance-youtube-oauth"}). Compara {@link MessageEvent.source} com a janela que
 * este hook abriu para não reagir ao popup de outra linha/instância na mesma tela.
 */
export function useYoutubeOAuthPopup({ start, getConnected, onConnected }: UseYoutubeOAuthPopupOptions) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const popupRef = useRef<Window | null>(null);
  // Evita que o listener de `message` e o polling de fallback disparem `onConnected`/toast
  // duas vezes para a mesma conexão (ambos podem resolver quase simultaneamente).
  const handledRef = useRef(false);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!event.data || event.data.source !== "meulance-youtube-oauth") return;
      if (event.source !== popupRef.current) return;
      popupRef.current?.close();
      popupRef.current = null;
      if (event.data.success) {
        if (handledRef.current) return;
        handledRef.current = true;
        await onConnected();
        toast.success(intl.formatMessage({ id: "youtubeConnection.connected" }));
      } else {
        toast.error(intl.formatMessage({ id: "global.generic_error" }));
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, onConnected]);

  const connect = useCallback(
    async (preOpenedPopup?: Window | null) => {
      setLoading(true);
      handledRef.current = false;
      const popupWindow =
        preOpenedPopup !== undefined
          ? preOpenedPopup
          : window.open("", "youtube-oauth", "width=520,height=650");
      const usePopup = !!popupWindow;
      try {
        const result = await start(usePopup);
        if (!result.authorizationUrl) {
          popupWindow?.close();
          toast.error(intl.formatMessage({ id: "global.generic_error" }));
          return;
        }
        if (usePopup && popupWindow) {
          popupWindow.location.href = result.authorizationUrl;
          popupRef.current = popupWindow;

          // Polling como fallback: verifica periodicamente se a conexão foi estabelecida
          const pollInterval = setInterval(async () => {
            try {
              if (!popupRef.current || handledRef.current) {
                clearInterval(pollInterval);
                return;
              }
              const connected = await getConnected();
              if (connected) {
                clearInterval(pollInterval);
                if (handledRef.current) return;
                handledRef.current = true;
                popupRef.current?.close();
                popupRef.current = null;
                await onConnected();
                toast.success(intl.formatMessage({ id: "youtubeConnection.connected" }));
              }
            } catch {
              // continua tentando
            }
          }, 2000);
          setTimeout(() => clearInterval(pollInterval), 300000);
          return;
        }
        window.location.href = result.authorizationUrl;
      } catch {
        popupWindow?.close();
        toast.error(intl.formatMessage({ id: "global.generic_error" }));
      } finally {
        setLoading(false);
      }
    },
    [start, getConnected, onConnected, intl]
  );

  return { connect, loading };
}

export default useYoutubeOAuthPopup;
